
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import reportWebVitals from './reportWebVitals';

import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { getMsalConfig } from './config/authConfig';
import { loadRuntimeConfig } from './config/runtimeConfig';
import { initTelemetry, reactPlugin, getAppInsights } from './config/telemetry';

const root = ReactDOM.createRoot(document.getElementById('root'));

const initializeApp = async () => {
  await loadRuntimeConfig();

  // Start Application Insights telemetry (no-op when connection string is absent)
  initTelemetry();

  /**
   * MSAL instance created following:
   * https://learn.microsoft.com/en-us/entra/identity-platform/tutorial-single-page-app-react-prepare-app
   */
  const msalInstance = new PublicClientApplication(getMsalConfig());

  // Initialize MSAL, then render the app inside MsalProvider
  await msalInstance.initialize();

  // Complete any pending redirect flow (auth code exchange) before rendering.
  const redirectResponse = await msalInstance.handleRedirectPromise();
  if (redirectResponse?.account) {
    msalInstance.setActiveAccount(redirectResponse.account);
  }

  // Set the first account as active if none is already active (e.g. page refresh)
  if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  // Automatically set the active account after every successful login
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });

  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <AppInsightsContext.Provider value={reactPlugin}>
          <MsalProvider instance={msalInstance}>
            <App />
          </MsalProvider>
        </AppInsightsContext.Provider>
      </ErrorBoundary>
    </React.StrictMode>
  );
};

initializeApp().catch((error) => {
  console.error('Failed to initialize application:', error);
  const ai = getAppInsights();
  if (ai) ai.trackException({ exception: error, properties: { component: 'App', action: 'initialization' } });
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
